// Import React for the hook
import React from "react";

/**
 * Utility to handle ResizeObserver errors that commonly occur in React applications
 * especially when using libraries like ReactFlow.
 *
 * The ResizeObserver error "ResizeObserver loop completed with undelivered notifications"
 * is usually harmless but can spam the console. This utility provides a clean way to handle it.
 */

export class ResizeObserverErrorHandler {
  private static isHandlerInstalled = false;
  private static handlerCount = 0;

  /**
   * Install the global error handler for ResizeObserver errors
   */
  static install(): void {
    if (typeof window === "undefined" || this.isHandlerInstalled) {
      this.handlerCount++;
      return;
    }

    window.addEventListener("error", this.handleError);
    this.isHandlerInstalled = true;
    this.handlerCount = 1;
  }

  /**
   * Remove the global error handler
   */
  static uninstall(): void {
    if (typeof window === "undefined" || !this.isHandlerInstalled) {
      return;
    }

    this.handlerCount--;

    // Only remove if no other Panels are using it
    if (this.handlerCount <= 0) {
      window.removeEventListener("error", this.handleError);
      this.isHandlerInstalled = false;
      this.handlerCount = 0;
    }
  }

  /**
   * Handle the error event
   */
  private static handleError = (e: ErrorEvent): boolean | void => {
    if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
      // Prevent the error from being logged to console
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };
}

/**
 * React hook to automatically install and cleanup ResizeObserver error handler
 */
export const useResizeObserverErrorHandler = (): void => {
  React.useEffect(() => {
    ResizeObserverErrorHandler.install();

    return () => {
      ResizeObserverErrorHandler.uninstall();
    };
  }, []);
};
